import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { addSnackbarAlert, removeAllSnackbarAlerts } from '@sternum/ui';
import { Icon } from '@sternum/web/components';
import { namespaces } from '@sternum/web/services/i18n';
import { makeErrorMessage, TRPCReactQueryError } from '@sternum/web/services/trpc';
import { extractRateLimitFromError } from '@sternum/web/utils';

import { useMFATokenFromUrl, useResendMFA } from '../../hooks';
import { Countdown } from '../Countdown';

import { ButtonRefresh } from './MFAForm.styles';

export interface ResendMFAButtonProps {
  disabled?: boolean;
  onSuccess?: () => unknown;
}

export const ResendMFAMutationKey = 'resend-mfa-mutation';

export function ResendMFAButton({ disabled = false, onSuccess }: ResendMFAButtonProps) {
  const { t } = useTranslation(namespaces.auth);
  const { token } = useMFATokenFromUrl();
  const [resendRemainingAttempts, setResendRemainingAttempts] = useState<number | null>(null);

  const { mutateAsync: resendMFA, isLoading: isResendInProgress } = useResendMFA({
    mutationKey: [ResendMFAMutationKey],
  });

  const handleResendClick = async () => {
    if (!token) return;

    try {
      await resendMFA({ jwtToken: token });

      onSuccess?.();
    } catch (err) {
      const error = err as TRPCReactQueryError;

      const { rateLimit, rateLimitRemaining, rateLimitReset } = extractRateLimitFromError(error);

      setResendRemainingAttempts(rateLimitRemaining);

      if (rateLimitRemaining > 0) {
        addSnackbarAlert({ variant: 'warning', message: t('resendMfaAttempts', { count: rateLimitRemaining }) });
      }

      if (rateLimitRemaining === 0) {
        removeAllSnackbarAlerts();
        addSnackbarAlert({
          variant: 'error',
          message: (
            <Countdown
              translationKey="resendMfaThrottled"
              initialValueInMs={rateLimitReset.getTime() - Date.now()}
              rateLimit={rateLimit}
            />
          ),
          displayTime: -1,
        });
      } else {
        const errorMessage = makeErrorMessage(error, t);

        addSnackbarAlert({ variant: 'error', message: errorMessage });
      }
    }
  };

  return (
    <ButtonRefresh
      type="button"
      variant="text-secondary"
      leftIcon={<Icon size={12} type="RefreshIcon" />}
      disabled={disabled || isResendInProgress || resendRemainingAttempts === 0}
      onClick={handleResendClick}
    >
      {t('mfaResendButton')}
    </ButtonRefresh>
  );
}
